import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

const MOYASAR_JS = '/vendor/moyasar/moyasar.umd.min.js';
const MOYASAR_CSS = '/vendor/moyasar/moyasar.css';

const ensureCss = () => {
    if (document.querySelector(`link[href="${MOYASAR_CSS}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = MOYASAR_CSS;
    document.head.appendChild(link);
};

const ensureScript = () => {
    if (!document.querySelector(`script[src="${MOYASAR_JS}"]`)) {
        const s = document.createElement('script');
        s.src = MOYASAR_JS;
        s.async = true;
        document.head.appendChild(s);
    }
};

// Wait until window.Moyasar is available (the UMD bundle sets it after eval).
const waitForMoyasar = (timeout = 8000, interval = 100) =>
    new Promise((resolve, reject) => {
        const start = Date.now();
        const check = () => {
            if (window.Moyasar) return resolve(window.Moyasar);
            if (Date.now() - start > timeout) {
                return reject(new Error('Moyasar SDK not available'));
            }
            setTimeout(check, interval);
        };
        check();
    });

/**
 * Renders the Moyasar hosted payment form.
 * config: { publishable_key, amount, currency, description, metadata, callback_url }
 */
export const MoyasarForm = ({ config }) => {
    const containerRef = useRef(null);
    const initializedRef = useRef(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            try {
                ensureCss();
                ensureScript();
                await waitForMoyasar();
                if (cancelled || initializedRef.current) return;
                const formEl = document.querySelector('.mysr-form');
                if (formEl) formEl.innerHTML = '';

                window.Moyasar.init({
                    element: '.mysr-form',
                    amount: config.amount,
                    currency: config.currency || 'SAR',
                    description: config.description,
                    publishable_api_key: config.publishable_key,
                    callback_url: config.callback_url,
                    methods: ['creditcard', 'applepay'],
                    apple_pay: {
                        country: 'SA',
                        label: 'Natijaty',
                        validate_merchant_url: 'https://api.moyasar.com/v1/applepay/initiate',
                    },
                    metadata: config.metadata || {},
                });
                initializedRef.current = true;
                setLoading(false);
            } catch (e) {
                if (!cancelled) {
                    setError(e.message || 'Failed to load payment form');
                    setLoading(false);
                }
            }
        };

        init();
        return () => {
            cancelled = true;
        };
    }, [config]);

    return (
        <div data-testid="moyasar-form-wrapper">
            {loading && (
                <div className="flex items-center justify-center py-10" data-testid="moyasar-loading">
                    <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
                </div>
            )}
            {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="moyasar-error">
                    {error}
                </div>
            )}
            <div ref={containerRef} className="mysr-form" />
        </div>
    );
};

export default MoyasarForm;
